// src/Components/bookingCalendar/TermAndCondition.jsx
import React from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

const TermAndCondition = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[80vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-green-600 p-6 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold tracking-tight">Terms and Conditions</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 text-gray-700 leading-relaxed">
          <h3 className="text-xl font-semibold mb-4">Mantra Mountain TreksTerms and Conditions</h3>
          <p className="mb-4">
            Welcome to Mantra Mountain. By booking a trek, tour, or package with us, you agree to be bound by the following Terms and Conditions. Please read them carefully before confirming your booking.
          </p>

          <h4 className="text-lg font-medium mt-6 mb-2">1. Booking and Payment</h4>
          <ul className="list-disc pl-6 mb-4">
            <li>
              All bookings are subject to availability and are confirmed only upon receipt of full payment or a deposit as specified at the time of booking.
            </li>
            <li>
              Payments are non-refundable except in cases outlined in our Cancellation Policy below.
            </li>
            <li>
              You must provide accurate personal information, including name, contact details, and any relevant medical conditions that may affect your participation.
            </li>
          </ul>

          <h4 className="text-lg font-medium mt-6 mb-2">2. Cancellation Policy</h4>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Non-Refundable Policy:</strong> All payments made for bookings are non-refundable. Once a booking is confirmed and payment is processed, customers cannot request a refund under any circumstances, except as noted below.
            </li>
            <li>
              <strong>Cancellations by Mantra Mountain:</strong> In the event that Mantra Mountain Trekscancels a trip due to unforeseen circumstances, such as severe weather, natural disasters, political instability, technical issues, or operational constraints, we will:
              <ul className="list-circle pl-6 mt-2">
                <li>
                  Offer to reschedule your trip to a later date, subject to availability.
                </li>
                <li>
                  Provide a full refund if rescheduling is not possible or acceptable to you.
                </li>
              </ul>
            </li>
            <li>
              Customers are responsible for any costs incurred due to cancellations outside of Mantra Mountain’s control (e.g., flight cancellations, visa fees).
            </li>
          </ul>

          <h4 className="text-lg font-medium mt-6 mb-2">3. Insurance</h4>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Mandatory Travel Insurance:</strong> All participants are required to obtain comprehensive travel insurance that covers trekking, adventure activities, medical emergencies, trip cancellations, and repatriation. Proof of insurance may be requested prior to departure.
            </li>
            <li>
              Mantra Mountain Treksdoes not provide insurance for customers. However, we maintain insurance for our guides and staff to ensure their safety and well-being.
            </li>
            <li>
              Customers are responsible for ensuring their insurance policy is adequate for the activities involved, including high-altitude trekking if applicable.
            </li>
          </ul>

          <h4 className="text-lg font-medium mt-6 mb-2">4. Health and Safety</h4>
          <ul className="list-disc pl-6 mb-4">
            <li>
              Trekking and adventure activities involve inherent risks, including but not limited to altitude sickness, physical injury, and environmental hazards. By booking, you acknowledge and accept these risks.
            </li>
            <li>
              You must disclose any pre-existing medical conditions or dietary requirements at the time of booking. Failure to do so may result in your inability to participate without a refund.
            </li>
            <li>
              Mantra Mountain Treksreserves the right to refuse participation to anyone deemed unfit or unprepared for the activity, prioritizing the safety of all participants.
            </li>
          </ul>

          <h4 className="text-lg font-medium mt-6 mb-2">5. Liability</h4>
          <ul className="list-disc pl-6 mb-4">
            <li>
              Mantra Mountain, its employees, guides, and affiliates are not liable for any injury, loss, damage, delay, or inconvenience experienced during the trip, except where such liability cannot be excluded by law.
            </li>
            <li>
              Customers are responsible for their personal belongings and any damages caused by their actions during the trip.
            </li>
            <li>
              All itineraries are subject to change due to weather, local conditions, or other factors beyond our control. Mantra Mountain Trekswill make reasonable efforts to provide alternative arrangements but is not liable for any resulting changes.
            </li>
          </ul>

          <h4 className="text-lg font-medium mt-6 mb-2">6. Customer Responsibilities</h4>
          <ul className="list-disc pl-6 mb-4">
            <li>
              You are responsible for obtaining all necessary travel documents, including passports, visas, and permits, as required for the trip.
            </li>
            <li>
              Compliance with local laws, customs, and guidelines provided by Mantra Mountain Treksor its guides is mandatory.
            </li>
            <li>
              You agree to follow the instructions of guides and staff to ensure the safety and enjoyment of all participants.
            </li>
          </ul>

          <h4 className="text-lg font-medium mt-6 mb-2">7. Force Majeure</h4>
          <p className="mb-4">
            Mantra Mountain Treksis not liable for failure to perform its obligations if such failure is due to events beyond our control, including but not limited to natural disasters, political unrest, pandemics, or other force majeure events. In such cases, we will work to reschedule your trip or provide a refund as per our Cancellation Policy.
          </p>

          <h4 className="text-lg font-medium mt-6 mb-2">8. Agreement</h4>
          <p className="mb-4">
            By checking the “I agree to the Terms and Conditions” box during booking, you confirm that you have read, understood, and agree to be bound by these Terms and Conditions. These terms constitute the entire agreement between you and Mantra Mountain.
          </p>

          <p className="mt-6 text-sm">
            For any questions or clarifications, please contact us at{' '}
            <a href="mailto:info@mantramountain.com" className="text-indigo-600 hover:underline">
              info@mantramountain.com
            </a>.
          </p>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-indigo-600 text-white font-semibold rounded-full hover:bg-indigo-700 transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

TermAndCondition.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TermAndCondition;